import React, { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { ptBR } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Date field that works identically on every device (iOS/Android/desktop).
 * Replaces `<Input type="date">` which iOS renders with intrinsic width and
 * centered text that no CSS can fully normalize.
 *
 * Props:
 *   - value: "YYYY-MM-DD" string (or empty)
 *   - onChange: (newIso: string) => void
 *   - placeholder: fallback label when value is empty
 *   - fromYear / toYear: range for the year dropdown (defaults: 2000 → current+1)
 *   - className, testid: forwarded to the trigger button
 */
function parseIso(v) {
  if (!v || typeof v !== "string") return null;
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

function toIso(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function formatBR(d) {
  if (!d) return "";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function DateFieldPT({
  value,
  onChange,
  placeholder = "Selecione uma data",
  className = "",
  testid,
  disabled = false,
  fromYear,
  toYear,
}) {
  const date = parseIso(value);
  const [open, setOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const _fromYear = fromYear ?? 2000;
  const _toYear = toYear ?? currentYear + 1;
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "flex h-9 w-full min-w-0 max-w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-left transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent",
            "hover:bg-white",
            disabled && "opacity-60 cursor-not-allowed",
            className,
          )}
        >
          <span className={cn("truncate", !date && "text-slate-400")}>
            {date ? formatBR(date) : placeholder}
          </span>
          <CalendarIcon className="w-4 h-4 text-slate-500 shrink-0 ml-2" strokeWidth={2.2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="p-0 w-auto z-[100] max-h-[80vh] overflow-y-auto"
      >
        <Calendar
          mode="single"
          locale={ptBR}
          selected={date || undefined}
          defaultMonth={date || undefined}
          captionLayout="dropdown-buttons"
          fromYear={_fromYear}
          toYear={_toYear}
          onSelect={(d) => {
            onChange?.(toIso(d));
            setOpen(false);
          }}
          initialFocus
          classNames={{
            caption: "flex justify-center pt-1 pb-2 relative items-center gap-1",
            caption_label: "hidden",
            caption_dropdowns: "flex items-center gap-1.5",
            dropdown:
              "h-8 rounded-md border border-slate-200 bg-white px-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#7c3aed] cursor-pointer",
            dropdown_month: "pr-1",
            dropdown_year: "pr-1",
            vhidden: "hidden",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
